import { notify } from '@billy/design-system'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useIsDemoInvoiceEditorEnabled } from '@features/demoInvoice/hooks/useIsDemoInvoiceEditorEnabled'
import { DownloadDocumentFileType } from '@features/invoices/enums/downloadDocumentFileType'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { SendAsEmailModalVariant } from '@features/invoices/enums/sendAsEmailModalVariant'
import { useSendAsEInvoice } from '@features/invoices/hooks/sendAsEInvoice/useSendAsEInvoice'
import { useSendAsEmail } from '@features/invoices/hooks/sendAsEmail/useSendAsEmail'
import { useDownloadInvoice } from '@features/invoices/hooks/useDownloadInvoice'
import { useInvalidateInvoices } from '@features/invoices/hooks/useInvalidateInvoices'
import { InvoiceForm } from '@features/invoices/types/invoiceForm/invoiceForm'
import { SendAsEmailForm } from '@features/invoices/types/sendAsEmailForm'
import { getInvoiceNumberDisplayedValue } from '@features/invoices/utils/getInvoiceNumberDisplayedValue'
import { usePayNowStatus } from '@features/paynow/hooks/usePayNowStatus'
import { QuickStartStep } from '@features/quickStart/enums'
import { useQuickStartProgress } from '@features/quickStart/hooks/useQuickStartProgress'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../config/queryClient'
import { useDirtyRoute } from '../../../../../contexts/dirtyRouteContext'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { useUserflow } from '../../../../../hooks'
import { getOrganizationViewUrl } from '../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../utils/routing/reactRoute'
import { useInvoiceEditorForm } from '../contexts/invoiceEditorFormContext'
import { useSideActionsModals } from '../contexts/sideActionsModalsContext'
import { FinalizeActionDropdownItem } from '../types/finalizeActionDropdownItem'
import { getDocumentApprovalNotification } from '../utils/getDocumentApprovalNotification'
import { useInvoiceEditorDocumentType } from './useInvoiceEditorDocumentType'
import { useSaveAndApproveInvoice } from './useSaveAndApproveInvoice'

interface FinalizeActionOptions {
  formData: InvoiceForm
  invoiceId: number
  onSuccess?: (invoiceId: number) => void
}

type FinalizeAction = (props: FinalizeActionOptions) => void

interface ApproveAndCloseOptions {
  onSuccess?: (invoiceId: number) => void
}

type DownloadInvoiceAsPdfOptions = ApproveAndCloseOptions

type DownloadInvoiceAsXRechnungOptions = ApproveAndCloseOptions

interface SendAsEmailOptions extends ApproveAndCloseOptions {
  sendAsEmailFormData: SendAsEmailForm
}
interface SendAsEinvoiceOptions extends ApproveAndCloseOptions {
  addressingLine?: string
  attachmentUuids?: string[]
}

const USERFLOW_SHINE_NL_INVOICE_CREATION_SURVEY_FLOW_ID = 'd72aca41-ca7e-4f23-994e-dccd46b07ce9'

export const useInvoiceEditorFormSubmit = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { setDirty } = useDirtyRoute()
  const { organization } = useUserOrganization()
  const { start } = useUserflow()
  const { documentType, isInvoice, isQuote } = useInvoiceEditorDocumentType()
  const { markStepDone } = useQuickStartProgress()
  const isDemoInvoiceEditorEnabled = useIsDemoInvoiceEditorEnabled()
  // Users offered the demo see "Try the demo" instead of "Start with a quote", so only a real invoice completes the step
  const shouldMarkInvoiceStepDone = isInvoice || (isQuote && !isDemoInvoiceEditorEnabled)

  const { isActive: isStripeActive, isLoading: isPayNowStatusLoading } = usePayNowStatus()

  const { saveAndApproveInvoice, isLoading: isSaveAndApproveLoading } = useSaveAndApproveInvoice()

  const isLoading = isSaveAndApproveLoading || isPayNowStatusLoading
  const { download } = useDownloadInvoice()
  const { invoiceEditorForm } = useInvoiceEditorForm()
  const { closeApprovalModal } = useSideActionsModals()
  const { invalidateExternalInvoices } = useInvalidateInvoices()

  const redirectToDocumentView = useCallback(
    (documentId?: number) => {
      if (documentId !== undefined) {
        const documentViewRoute =
          documentType === InvoiceDocumentType.Quote
            ? reactRoute.organization.quotes.getViewQuoteRoute(String(documentId))
            : reactRoute.organization.invoices.getViewInvoiceRoute(String(documentId), documentType)

        navigate(`${getOrganizationViewUrl(organization?.url, documentViewRoute)}`)
      }
    },
    [documentType, navigate, organization?.url],
  )

  const { sendAsEmail: sendInvoiceAsEmail } = useSendAsEmail(SendAsEmailModalVariant.Invoice, {
    onError: redirectToDocumentView,
    onSuccess: redirectToDocumentView,
  })

  const { sendAsEInvoice: sendInvoiceAsEInvoice } = useSendAsEInvoice({
    onError: redirectToDocumentView,
    onSuccess: redirectToDocumentView,
  })

  const { getValues } = invoiceEditorForm

  const resetIsSubmitting = () => setIsSubmitting(false)

  const resetAndRedirect = useCallback(
    (invoiceId: number) => {
      resetIsSubmitting()
      redirectToDocumentView(invoiceId)
    },
    [redirectToDocumentView],
  )

  // "wrapper function" for all finalization actions
  const finalize = useCallback(
    (finalizeAction: FinalizeAction) => {
      const formData = getValues()
      setIsSubmitting(true)
      setDirty(false)

      const onSuccess = (invoiceId: number) => {
        const successNotification = getDocumentApprovalNotification(documentType, t)

        invalidateExternalInvoices()

        queryClient.invalidateQueries(QueryKeys.Quotes)
        queryClient.invalidateQueries(QueryKeys.InvoicesSummary)
        queryClient.invalidateQueries(QueryKeys.ExternalInvoiceNumberingSequences)
        queryClient.invalidateQueries(QueryKeys.NextQuoteNumber)
        queryClient.invalidateQueries(QueryKeys.QuotesSummary)

        if (formData.approvedEstimateId) {
          queryClient.invalidateQueries([QueryKeys.Quote, formData.approvedEstimateId])
        }

        if (shouldMarkInvoiceStepDone) {
          markStepDone(QuickStartStep.Invoice)
        }

        finalizeAction?.({ formData, invoiceId })
        notify({
          id: NotificationKeys.ExternalInvoiceApproved,
          message: successNotification,
          variant: 'success',
        })
        start(USERFLOW_SHINE_NL_INVOICE_CREATION_SURVEY_FLOW_ID, { once: true })
      }

      const onError = () => {
        resetIsSubmitting()
        closeApprovalModal()
      }

      saveAndApproveInvoice({
        formData,
        onSuccess,
        onError,
      })
    },
    [
      closeApprovalModal,
      documentType,
      getValues,
      invalidateExternalInvoices,
      markStepDone,
      saveAndApproveInvoice,
      shouldMarkInvoiceStepDone,
      setDirty,
      start,
      t,
    ],
  )

  const approveAndClose = useCallback(
    ({ onSuccess }: ApproveAndCloseOptions = {}) => {
      finalize(({ invoiceId }: FinalizeActionOptions) => {
        onSuccess?.(invoiceId)
        resetAndRedirect(invoiceId)
      })
    },
    [finalize, resetAndRedirect],
  )

  const downloadAsPdf = useCallback(
    ({ onSuccess }: DownloadInvoiceAsPdfOptions = {}) => {
      finalize(({ invoiceId, formData }: FinalizeActionOptions) => {
        download({
          downloadDocumentFileType: DownloadDocumentFileType.PDF,
          documentLocale: formData.language,
          documentType,
          invoiceId: String(invoiceId),
          invoiceNumber: getInvoiceNumberDisplayedValue(formData.invoiceNumber, formData.invoiceNumberPrefix),
          onError: () => {
            resetAndRedirect(invoiceId)
          },
          onSuccess: () => {
            onSuccess?.(invoiceId)
            resetAndRedirect(invoiceId)
          },
        })
      })
    },
    [download, finalize, documentType, resetAndRedirect],
  )

  const downloadAsXRechnung = useCallback(
    ({ onSuccess }: DownloadInvoiceAsXRechnungOptions = {}) => {
      finalize(({ invoiceId, formData }: FinalizeActionOptions) => {
        download({
          downloadDocumentFileType: DownloadDocumentFileType.XML,
          documentLocale: formData.language,
          documentType,
          invoiceId: String(invoiceId),
          invoiceNumber: getInvoiceNumberDisplayedValue(formData.invoiceNumber, formData.invoiceNumberPrefix),
          onError: () => {
            resetAndRedirect(invoiceId)
          },
          onSuccess: () => {
            onSuccess?.(invoiceId)
            notify({
              id: NotificationKeys.ExternalInvoiceDownloadAsXml,
              message: t('external_invoices.download_as_xrechnung.notification.success'),
              variant: 'success',
            })
            resetAndRedirect(invoiceId)
          },
        })
      })
    },
    [documentType, download, finalize, resetAndRedirect, t],
  )

  const sendAsEmail = useCallback(
    ({ onSuccess, sendAsEmailFormData }: SendAsEmailOptions) => {
      finalize(async ({ formData, invoiceId }: FinalizeActionOptions) => {
        onSuccess?.(invoiceId)
        const acceptStripePayment = isStripeActive && Boolean(formData.displayedPaymentMethods.stripeCardPayment)
        sendInvoiceAsEmail({ acceptStripePayment, documentType, formData: sendAsEmailFormData, invoiceId })
      })
    },
    [finalize, documentType, isStripeActive, sendInvoiceAsEmail],
  )

  const sendAsEInvoice = useCallback(
    ({ addressingLine, attachmentUuids, onSuccess }: SendAsEinvoiceOptions = {}) => {
      finalize(({ invoiceId, formData }: FinalizeActionOptions) => {
        onSuccess?.(invoiceId)

        const { publicEntityRoutingEnabled, name: companyName = '' } = formData.receiver || {}
        sendInvoiceAsEInvoice({
          addressingLine,
          attachmentUuids,
          companyName,
          documentType,
          invoiceId,
          isPublicEntity: !!publicEntityRoutingEnabled,
        })
      })
    },
    [documentType, finalize, sendInvoiceAsEInvoice],
  )

  const submit: Record<FinalizeActionDropdownItem, Function> = useMemo(
    () => ({
      approveAndClose,
      downloadAsPdf,
      downloadAsXRechnung,
      sendAsEmail,
      sendAsEInvoice,
    }),
    [approveAndClose, downloadAsPdf, downloadAsXRechnung, sendAsEmail, sendAsEInvoice],
  )

  return {
    isLoading,
    isSubmitting,
    submit,
  }
}
